<template>
  <div class="amc-plan-details-container">
    <div class="amc-header">
      <div class="header-left">
        <i class="el-icon-arrow-left back-btn" @click="goBack"></i>
        <h1 class="page-title">设置备考计划</h1>
      </div>
    </div>

    <div class="content-area">
      <div class="summary">
        <div class="summary-item">
          <span class="label">级别</span>
          <span class="value">{{ levelLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="label">知识背景</span>
          <span class="value">{{ backgroundLabel }}</span>
        </div>
        <div class="summary-item">
          <span class="label">参赛年份</span>
          <span class="value">{{ routeQuery.year }}年</span>
        </div>
        <div class="summary-item">
          <span class="label">考试时间</span>
          <span class="value">{{ examDateDisplay || '待配置' }}</span>
        </div>
      </div>

      <el-form :model="form" label-width="120px" class="plan-form">
        <el-form-item label="每周学习日(多选)">
          <el-select v-model="form.studyDayList" multiple collapse-tags placeholder="选择每周学习的星期几">
            <el-option v-for="d in weekOptions" :key="d.value" :label="d.label" :value="d.value" />
          </el-select>
        </el-form-item>

        <el-form-item label="每天学习时长(分钟)">
          <el-input-number v-model="form.dailyMinutes" :min="15" :max="240" :step="15" controls-position="right"/>
        </el-form-item>

        <el-form-item label="绝对休息日">
          <el-date-picker
            v-model="form.restDates"
            type="dates"
            :editable="false"
            :clearable="true"
            :picker-options="{ disabledDate: disableRestDate }"
            placeholder="选择休息日期（可多选）"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            style="width: 100%"
          />
          <div class="tip">可多选，范围：今日起至考试日前（{{ examDateDisplay }}）</div>
        </el-form-item>

        <el-form-item label="最后冲刺(天)">
          <el-input-number v-model="form.sprintDays" :min="0" :max="120" :step="1" controls-position="right"/>
        </el-form-item>

        <div class="action-section">
          <el-button type="primary" class="start-button" @click="openConfirm" :loading="calcLoading">
            开始AI备考
          </el-button>
        </div>
      </el-form>
    </div>

    <el-dialog :visible.sync="confirmVisible" title="确认备考信息" width="500px">
      <div class="confirm-list">
        <div class="confirm-item"><strong>AMC级别：</strong>{{ levelLabel }}</div>
        <div class="confirm-item"><strong>知识背景：</strong>{{ backgroundLabel }}</div>
        <div class="confirm-item"><strong>参赛年份：</strong>{{ routeQuery.year }}年</div>
        <div class="confirm-item"><strong>考试时间：</strong>{{ examDateDisplay || '待配置' }}</div>
        <div class="confirm-item"><strong>每周学习日：</strong>{{ studyDaysPerWeekText }}</div>
        <div class="confirm-item"><strong>每天学习时长：</strong>{{ form.dailyMinutes }} 分钟</div>
        <div class="confirm-item"><strong>绝对休息日：</strong>{{ restDatesLabel }}</div>
        <div class="confirm-item"><strong>最后冲刺：</strong>{{ form.sprintDays }} 天</div>
        <div v-if="calcResult" class="confirm-item" style="margin-top:8px; border-top:1px dashed #e5e7eb; padding-top:8px;"><strong>智能计算结果：</strong></div>
        <template v-if="calcResult">
          <div class="confirm-item"><strong>题库ID：</strong>{{ calcResult.repoId }}</div>
          <div class="confirm-item"><strong>知识背景(数值)：</strong>{{ calcResult.level }}</div>
          <div class="confirm-item"><strong>考试日：</strong>{{ calcExamDateText }}</div>
          <div class="confirm-item"><strong>开始日：</strong>{{ calcStartDateText }}</div>
          <div class="confirm-item"><strong>结束日：</strong>{{ calcEndDateText }}<span v-if="calcResult.endDateSource">（{{ calcResult.endDateSource }}）</span></div>
          <div class="confirm-item"><strong>总题数：</strong>{{ calcResult.totalRepoCount }}</div>
          <div class="confirm-item"><strong>每题分钟：</strong>{{ calcResult.minutesPerQuestion }}</div>
          <div class="confirm-item"><strong>有效学习天数：</strong>{{ calcResult.effectiveStudyDays }}</div>
          <div class="confirm-item"><strong>错误率：</strong>{{ calcResult.errorRate }}</div>
          <div class="confirm-item"><strong>可用学习时长(分钟)：</strong>{{ calcResult.availableStudyMinutes }}</div>
          <div class="confirm-item"><strong>每日学习分钟：</strong>{{ calcResult.dailyStudyMinutes }}</div>
          <div class="confirm-item"><strong>已可覆盖独立题目：</strong>{{ calcResult.uniqueCoveredQuestions }}</div>
          <div class="confirm-item"><strong>预计总做题数：</strong>{{ calcResult.totalSolvedQuestions }}</div>
          <div class="confirm-item"><strong>深度：</strong>{{ calcResult.depth }}</div>
        </template>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmVisible=false">取消</el-button>
        <el-button type="primary" @click="confirmAndStart">确认开始</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getTokenInfo } from '@/utils/jwtUtils'
import { updateUserStatus } from '@/utils/userStatus'
import { fetchPaging as fetchRepoList } from '@/api/repo'
import { matchExamScheduleConfig } from '@/api/examScheduleConfig'
import {
  createPlan,
  updatePlan,
  getPlanByUserIdAndRepoId,
  generateScheme,
  getSchemeByUserIdAndRepoId,
  upsertTodayPlan,
  calcStudyDepth
} from '@/api/studyPlan'

export default {
  name: 'AMCPlanDetails',
  data() {
    return {
      routeQuery: {
        level: this.$route.query.level,
        levelTitle: this.$route.query.levelTitle || this.$route.query.level,
        subjectCode: this.$route.query.subjectCode || this.$route.query.level,
        knowledgeBackground: this.$route.query.knowledgeBackground,
        year: Number(this.$route.query.year),
        courseTypeId: this.$route.query.courseTypeId || null,
        courseTypeCode: this.$route.query.courseTypeCode || null,
        sessionCode: this.$route.query.sessionCode || null,
        repoId: this.$route.query.repoId || null
      },
      form: {
        studyDayList: [1,2,3,4,5],
        dailyMinutes: 60,
        restDates: [],
        sprintDays: 14
      },
      confirmVisible: false,
      examDate: null,
      repoIdResolved: null,
      calcLoading: false,
      weekOptions: [
        { value: 1, label: '周一' },
        { value: 2, label: '周二' },
        { value: 3, label: '周三' },
        { value: 4, label: '周四' },
        { value: 5, label: '周五' },
        { value: 6, label: '周六' },
        { value: 7, label: '周日' }
      ],
      calcResult: null
    }
  },
  created() {
    this.hideSidebarAndNavbar()
    this.initExamDate()
  },
  beforeDestroy() {
    this.showSidebarAndNavbar()
  },
  computed: {
    isAmcMode() {
      const code = String(this.routeQuery.courseTypeCode || '').toLowerCase()
      if (code && code.includes('amc')) return true
      const lv = String(this.routeQuery.level || '').toLowerCase()
      return lv === 'amc8' || lv === 'amc10' || lv === 'amc12'
    },
    courseTypeTitle() {
      return String(this.routeQuery.courseTypeCode || '课程').toUpperCase()
    },
    levelLabel() {
      const map = { amc8: 'AMC 8', amc10: 'AMC 10', amc12: 'AMC 12' }
      return this.routeQuery.levelTitle || map[this.routeQuery.level] || this.routeQuery.level
    },
    backgroundLabel() {
      const map = { never: '从来没有', basic: '有一定了解', proficient: '熟练掌握' }
      return map[this.routeQuery.knowledgeBackground] || this.routeQuery.knowledgeBackground
    },
    restDatesLabel() {
      if (!this.form.restDates || this.form.restDates.length === 0) return '无'
      return this.form.restDates.join('、')
    },
    examDateDisplay() {
      return this.formatDateTime(this.examDate)
    },
    examDateText() {
      const d = this.examDate
      if (!d) return ''
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      return `${d.getFullYear()}-${mm}-${dd}`
    },
    examDateTimeText() {
      const d = this.examDate
      if (!d) return ''
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mi = String(d.getMinutes()).padStart(2, '0')
      const ss = String(d.getSeconds()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
    },
    studyDaysPerWeekText() {
      if (!this.form.studyDayList || this.form.studyDayList.length === 0) return '未选择'
      const map = { 1:'周一',2:'周二',3:'周三',4:'周四',5:'周五',6:'周六',7:'周日' }
      const sorted = [...this.form.studyDayList].sort((a,b) => a - b)
      return `${sorted.map(v => map[v]).join('、')}（共${sorted.length}天/周）`
    },
    errorRateOverrideText() {
      const val = (this.calcResult && this.calcResult.errorRateOverride) || this.getErrorRateOverride(this.routeQuery.level)
      return typeof val === 'number' ? val : Number(val)
    },
    calcExamDateText() {
      const s = this.calcResult && (this.calcResult.examDate || this.calcResult.exam_date)
      return this.formatDateStr(s)
    },
    calcStartDateText() {
      const s = this.calcResult && (this.calcResult.startDate || this.calcResult.start_date)
      return this.formatDateStr(s)
    },
    calcEndDateText() {
      const s = this.calcResult && (this.calcResult.endDate || this.calcResult.end_date)
      return this.formatDateStr(s)
    }
  },
  methods: {
    goBack() {
      this.$router.go(-1)
    },
    hideSidebarAndNavbar() {
      this.$store && this.$store.dispatch && this.$store.dispatch('app/closeSideBar', { withoutAnimation: false })
      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')
      if (sidebar) sidebar.style.display = 'none'
      if (navbar) navbar.style.display = 'none'
      const appMain = document.querySelector('.app-main')
      if (appMain) { appMain.style.padding = '0'; appMain.style.margin = '0' }
    },
    showSidebarAndNavbar() {
      this.$store && this.$store.dispatch && this.$store.dispatch('app/openSideBar', { withoutAnimation: false })
      const sidebar = document.querySelector('.sidebar-container')
      const navbar = document.querySelector('.navbar')
      if (sidebar) sidebar.style.display = ''
      if (navbar) navbar.style.display = ''
      const appMain = document.querySelector('.app-main')
      if (appMain) { appMain.style.padding = ''; appMain.style.margin = '' }
    },
    async openConfirm() {
      if (!this.routeQuery.level || !this.routeQuery.knowledgeBackground || !this.routeQuery.year) {
        this.$message.warning('缺少上一步的选择，请返回重新选择')
        return
      }
      try {
        this.calcLoading = true
        // 动态解析 repoId（支持非 AMC）
        if (!this.repoIdResolved) {
          this.repoIdResolved = await this.resolveRepoIdFromRepoTable()
        }
        if (!this.repoIdResolved) {
          this.$message.error('未匹配到题库，请联系管理员配置题库')
          return
        }
        if (!this.examDate) {
          await this.initExamDate()
        }
        if (!this.examDate) {
          this.$message.error('未配置该考试的备考时间，请联系管理员在备考时间管理中配置')
          return
        }
        await this.callCalcStudyDepth()
        this.confirmVisible = true
      } catch (e) {
        console.error('calcStudyDepth 调用失败', e)
        this.$message.error('计算学习计划失败，请稍后重试')
      } finally {
        this.calcLoading = false
      }
    },
    async confirmAndStart() {
      try {
        await this.saveAll()
        this.confirmVisible = false
        this.$router.replace({
          path: '/amc-course',
          query: {
            level: this.routeQuery.level,
            knowledgeBackground: this.routeQuery.knowledgeBackground,
            year: this.routeQuery.year,
            courseTypeId: this.routeQuery.courseTypeId,
            courseTypeCode: this.routeQuery.courseTypeCode,
            sessionCode: this.routeQuery.sessionCode || undefined
          }
        })
      } catch (e) {
        console.error(e)
        this.$message.error('保存失败，请重试')
      }
    },
    async initExamDate() {
      try {
        const courseTypeCode = String(this.routeQuery.courseTypeCode || (this.isAmcMode ? 'AMC' : '')).toUpperCase()
        const subjectCode = String(this.routeQuery.subjectCode || this.routeQuery.level || '').toUpperCase()
        if (!courseTypeCode || !subjectCode || !this.routeQuery.year) {
          this.examDate = this.getExamDate(this.routeQuery.level, this.routeQuery.year)
          return
        }
        const res = await matchExamScheduleConfig({
          courseTypeCode,
          subjectCode,
          targetYear: this.routeQuery.year,
          sessionCode: this.routeQuery.sessionCode || undefined
        }).catch(() => null)
        const config = res && (res.data !== undefined ? res.data : res)
        const examDate = config && config.examDate
        if (examDate) {
          this.examDate = new Date(String(examDate).replace(' ', 'T'))
          if (!isNaN(this.examDate.getTime())) {
            return
          }
        }
        this.examDate = this.isAmcMode ? this.getExamDate(this.routeQuery.level, this.routeQuery.year) : null
      } catch (e) {
        this.examDate = this.isAmcMode ? this.getExamDate(this.routeQuery.level, this.routeQuery.year) : null
      }
    },

    async getRepoListCached() {
      const res = await fetchRepoList({ pageNum: 1, pageSize: 1000 }).catch(() => null)
      const list = Array.isArray(res?.data)
        ? res.data
        : (Array.isArray(res?.data?.data) ? res.data.data : (Array.isArray(res) ? res : []))
      return Array.isArray(list) ? list : []
    },
    async resolveRepoIdFromRepoTable() {
      // 优先使用路由中传入的 repoId
      const routeRepoId = Number(this.routeQuery.repoId || this.$route?.query?.repoId)
      if (routeRepoId && !Number.isNaN(routeRepoId)) {
        return routeRepoId
      }

      const titleRaw = (this.routeQuery.level || '').toString().trim()
      const titleKey = titleRaw.toLowerCase()
      const courseId = this.routeQuery.courseTypeId != null ? Number(this.routeQuery.courseTypeId) : NaN
      if (!titleKey) return null

      const rows = await this.getRepoListCached()
      const isDel = (r) => {
        const del = Number(r?.isDeleted != null ? r.isDeleted : r?.is_deleted)
        return del === 1
      }
      const normalizeRepoTitle = (value) => {
        const raw = String(value || '').trim()
        if (!raw) return ''
        const upper = raw.toUpperCase().replace(/\s+/g, '')
        if (upper === 'AMC8' || upper === 'AMC10' || upper === 'AMC12') return upper
        if (upper.startsWith('CCF-GESP') || upper.startsWith('GESP')) {
          const levelMatch = upper.match(/LEVEL(\d+)/) || upper.match(/^GESP(\d+)$/) || upper.match(/GESP.*?(\d+)/)
          if (levelMatch && levelMatch[1]) return `GESPLEVEL${levelMatch[1]}`
          return upper.replace(/^CCF-/, '')
        }
        return upper
      }
      const normalizedTitleKey = normalizeRepoTitle(titleRaw)
      const matchTitle = (t) => {
        const s = String(t || '').trim().toLowerCase()
        if (!s) return false
        if (s === titleKey) return true
        if (s.endsWith(titleKey) || s.includes(titleKey) || titleKey.includes(s)) return true
        const normalized = normalizeRepoTitle(t)
        if (!normalized || !normalizedTitleKey) return false
        if (normalized === normalizedTitleKey) return true
        return normalized.includes(normalizedTitleKey) || normalizedTitleKey.includes(normalized)
      }
      const matchCourseId = (r) => {
        if (!courseId || Number.isNaN(courseId)) return true
        const cid = Number(r?.courseId != null ? r.courseId : r?.course_id)
        return cid === courseId
      }

      let hit = rows.find(r => {
        if (!r) return false
        if (isDel(r)) return false
        if (!matchCourseId(r)) return false
        return matchTitle(r.title)
      })

      if (!hit) {
        hit = rows.find(r => {
          if (!r) return false
          if (isDel(r)) return false
          return matchTitle(r.title)
        })
      }

      const id = hit && hit.id != null ? Number(hit.id) : null
      return id && !Number.isNaN(id) ? id : null
    },
    async callCalcStudyDepth() {
      const tokenInfo = getTokenInfo() || {}
      const username = tokenInfo.username || tokenInfo.userName || tokenInfo.name || (JSON.parse(localStorage.getItem('userInfo') || '{}').username)
      // 使用通用学习计划接口的字段格式
      const payload = {
        userId: tokenInfo.id,
        username,
        repoId: Number(this.repoIdResolved || this.getAmcLevelNumber(this.routeQuery.level)),
        knowledgeLevel: this.getKnowledgeBackgroundNumber(this.routeQuery.knowledgeBackground),
        targetYear: this.routeQuery.year,
        isActive: 1,
        studyDaysPerWeek: (this.form.studyDayList || []).sort((a,b) => a - b).join(','),
        dailyStudyMinutes: Number(this.form.dailyMinutes),
        absoluteRestDates: Array.isArray(this.form.restDates) ? this.form.restDates.join(',') : '',
        finalSprintDays: Number(this.form.sprintDays),
        examDate: this.examDateTimeText || '',
        errorRateOverride: this.getErrorRateOverride(this.routeQuery.level),
        sessionCode: this.routeQuery.sessionCode || null
      }
      const res = await calcStudyDepth(tokenInfo.id, payload)
      // axios 拦截器已返回 response.data，这里兼容 {code,data,msg} 或直接返回数据两种格式
      const serverData = (res && (res.data !== undefined ? res.data : res)) || {}
      this.calcResult = { ...serverData, errorRateOverride: payload.errorRateOverride }
    },
    formatDateStr(s) {
      if (!s) return ''
      try {
        // 支持 '2026-01-22' 或 '2026-01-22T00:00'
        const d = new Date(s)
        if (isNaN(d.getTime())) return String(s)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd}`
      } catch (_) {
        return String(s)
      }
    },
    formatDateTime(value) {
      if (!value) return ''
      try {
        const d = value instanceof Date ? value : new Date(String(value).replace(' ', 'T'))
        if (isNaN(d.getTime())) return String(value)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const hh = String(d.getHours()).padStart(2, '0')
        const mi = String(d.getMinutes()).padStart(2, '0')
        const ss = String(d.getSeconds()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`
      } catch (_) {
        return String(value)
      }
    },
    getExamDate(level, year) {
      // AMC8: 1-22, AMC10: 11-03, AMC12: 11-15；非 AMC 兜底为当年 12-31
      const map = { amc8: { m: 0, d: 22 }, amc10: { m: 10, d: 3 }, amc12: { m: 10, d: 15 } }
      const key = String(level || '').toLowerCase()
      const rule = map[key] || { m: 11, d: 31 }
      return new Date(Number(year), rule.m, rule.d, 23, 59, 59, 999)
    },
    getErrorRateOverride(levelKey) {
      const map = { amc8: 0.2, amc10: 0.3, amc12: 0.4 }
      const key = String(levelKey || '').toLowerCase()
      return map[key] != null ? map[key] : 0.2
    },
    disableRestDate(time) {
      const today = new Date()
      today.setHours(0,0,0,0)
      const exam = this.examDate || today
      return time.getTime() < today.getTime() || time.getTime() > exam.getTime()
    },
    getAmcLevelNumber(level) {
      const levelMap = { 'amc8': 14, 'amc10': 15, 'amc12': 16 }
      return levelMap[level] || 14
    },
    getKnowledgeBackgroundNumber(background) {
      const backgroundMap = { 'never': 1, 'basic': 2, 'proficient': 3 }
      return backgroundMap[background] || 1
    },
    getCourseTypeId(level) {
      // 优先使用路由传入的课程类型ID
      const qid = this.routeQuery.courseTypeId
      if (qid != null && String(qid).trim() !== '') return Number(qid)
      // 兜底：AMC 使用 1
      const courseTypeMap = { 'amc8': 1, 'amc10': 1, 'amc12': 1 }
      return courseTypeMap[String(level || '').toLowerCase()] || 1
    },
    async saveAll() {
      // 1) 保存用户选择到后端（使用通用接口）
      const userInfo = getTokenInfo()
      if (!userInfo || !userInfo.id) {
        throw new Error('用户未登录或信息缺失')
      }
      const userId = userInfo.id
      if (!this.repoIdResolved) {
        this.repoIdResolved = await this.resolveRepoIdFromRepoTable()
      }
      const repoId = Number(this.repoIdResolved || this.getAmcLevelNumber(this.routeQuery.level))
      const levelNum = this.getKnowledgeBackgroundNumber(this.routeQuery.knowledgeBackground)

      // 获取课程类型ID（从题库关联的课程获取）
      const courseTypeId = this.getCourseTypeId(this.routeQuery.level)

      // 构建通用学习计划数据
      const planData = {
        userId,
        repoId,
        courseType: String(courseTypeId),
        knowledgeLevel: levelNum,
        targetYear: this.routeQuery.year,
        studyDaysPerWeek: Array.isArray(this.form.studyDayList) ? this.form.studyDayList.join(',') : '1,2,3,4,5',
        dailyStudyMinutes: Number(this.form.dailyMinutes),
        absoluteRestDates: Array.isArray(this.form.restDates) ? this.form.restDates.join(',') : '',
        finalSprintDays: Number(this.form.sprintDays),
        examDate: this.examDateTimeText || '',
        errorRateOverride: this.getErrorRateOverride(this.routeQuery.level),
        sessionCode: this.routeQuery.sessionCode || null,
        isActive: 1
      }

      // 检查是否已有计划，有则更新，无则创建
      try {
        const existingPlan = await getPlanByUserIdAndRepoId(userId, repoId, this.routeQuery.sessionCode)
        if (existingPlan && existingPlan.data && existingPlan.data.id) {
          await updatePlan({ id: existingPlan.data.id, ...planData })
        } else {
          await createPlan(planData)
        }
      } catch (e) {
        // 如果查询失败，尝试创建新计划
        await createPlan(planData)
      }

      try {
        updateUserStatus('mark', 1)
        updateUserStatus('skipCourseSelection', true)
      } catch (e) {
        try {
          const ui = JSON.parse(localStorage.getItem('userInfo') || '{}')
          ui.mark = 1
          ui.skipCourseSelection = true
          localStorage.setItem('userInfo', JSON.stringify(ui))
        } catch (_) {}
      }

      // 生成学习方案（使用通用接口，会自动生成 question_id）
      try {
        const ds = this.calcResult && (this.calcResult.depthSnapshot !== undefined ? this.calcResult.depthSnapshot : (this.calcResult.depth_snapshot !== undefined ? this.calcResult.depth_snapshot : this.calcResult.depth))
        const depthSnapshot = Math.max(0, Math.min(1, Number(ds)))
        if (!isNaN(depthSnapshot)) {
          // 使用 generateScheme 接口，会根据深度比例自动生成题目清单
          await generateScheme(userId, repoId, depthSnapshot)
        }
      } catch (e) { console.warn('生成学习方案失败（忽略）:', e) }

      // generateScheme 后端已自动将 t_user_question_record 中已做题目写入 finishedId，无需前端再处理
      localStorage.removeItem('preserveRecords')

      // 2.5) 强制更新当日计划（重置计划后需要刷新 planned_count）
      try {
        const schemeResp = await getSchemeByUserIdAndRepoId(userId, repoId)
        const scheme = schemeResp?.data
        const schemeId = scheme ? (scheme.id ?? scheme.schemeId) : null
        if (schemeId) {
          const today = new Date()
          const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
          await upsertTodayPlan({ userId, schemeId, repoId, date })
          console.log('重置计划后已更新当日计划')
        }
      } catch (e) { console.warn('更新当日计划失败（忽略）:', e) }

      // 3) 本地保存计划详情（包含课程类型ID和题库ID，便于主页读取）
      const plan = {
        studyDayList: this.form.studyDayList,
        dailyMinutes: this.form.dailyMinutes,
        restDates: this.form.restDates,
        sprintDays: this.form.sprintDays,
        // 同步保存后端字段视图
        studyDaysPerWeek: Array.isArray(this.form.studyDayList) ? this.form.studyDayList.length : 0,
        dailyStudyMinutes: Number(this.form.dailyMinutes),
        absoluteRestDates: Array.isArray(this.form.restDates) ? this.form.restDates.join(',') : '',
        finalSprintDays: Number(this.form.sprintDays),
        errorRateOverride: this.getErrorRateOverride(this.routeQuery.level),
        level: this.routeQuery.level,
        knowledgeBackground: this.routeQuery.knowledgeBackground,
        year: this.routeQuery.year,
        // 新增：课程类型ID和题库ID
        courseTypeId: courseTypeId,
        repoId: repoId,
        savedAt: new Date().toISOString()
      }
      localStorage.setItem('amc_study_plan', JSON.stringify(plan))
      // 同时单独存储课程类型ID和题库ID，便于其他页面快速读取
      localStorage.setItem('current_course_type_id', String(courseTypeId))
      localStorage.setItem('current_repo_id', String(repoId))
      this.$message.success('备考计划已保存')
    }
  }
}
</script>

<style scoped>
.amc-plan-details-container {
  min-height: 100vh;
  background-color: #f5f7fa;
}
.amc-header {
  background: #fff;
  padding: 15px 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}
.header-left { display: flex; align-items: center; gap: 12px; }
.back-btn { font-size: 20px; color: #666; cursor: pointer; padding: 5px; border-radius: 50%; }
.back-btn:hover { background: #f5f5f5; }
.page-title { margin: 0; font-size: 18px; font-weight: 600; color: #333; }

.content-area { max-width: 640px; margin: 0 auto; padding: 20px; }
.summary { background:#fff; border:1px solid #eaecef; border-radius:12px; padding:12px 16px; margin-bottom:16px; }
.summary-item { display:flex; justify-content: space-between; padding:6px 0; font-size:14px; }
.summary-item .label { color:#666; }
.summary-item .value { color:#333; font-weight:600; }
.plan-form { background:#fff; border:1px solid #eaecef; border-radius:12px; padding:16px; }
.action-section { text-align:center; margin-top: 12px; }
.start-button { width: 100%; height: 48px; font-weight: 600; border-radius: 8px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; }

.confirm-list { line-height: 1.8; }
.confirm-item { margin-bottom: 6px; }
.tip { margin-top: 6px; color: #909399; font-size: 12px; }
</style>
